import * as React from "react";
import PropTypes from "prop-types";
import { Button, CTALink, Link, Modal } from "@lululemon/ui-library";
import cs from "classnames";
import { LazyHydrate } from "@lululemon/mwa-perf";
import {
  toBraQuizSizePills,
  trackBqPDPInteraction,
  translatePills
} from "./utilities";
import { BraQuizResultBody } from "./bra-quiz-results-body";

import styles from "./bra-quiz-results.module.scss";

const BraQuizResults = ({
  braQuizResultsData,
  contentfulData,
  modalProps,
  isMobile,
  onCompleteQuiz,
  isEmbedded,
  truefitAtCDPABFlag,
  host,
  apiHost,
  proxyApiHost,
  links
}) => {
  React.useEffect(() => {
    if (isEmbedded || isMobile) {
      localStorage.removeItem("showBraQuizResultsModal");
    }
  }, []);
  const { description: braQuizDescription, title, sizeTexts } = contentfulData;
  const getTextPill = translatePills(sizeTexts);
  const truefitDescription = braQuizDescription.replace(/^\S+/g, "True Fit");

  const pills = toBraQuizSizePills(braQuizResultsData, getTextPill);
  const resultsBody = (
    <BraQuizResultBody
      {...{
        description: truefitAtCDPABFlag
          ? truefitDescription
          : braQuizDescription,
        isEmbedded,
        pills,
        title
      }}
    />
  );

  if (isEmbedded) {
    const { pdpPrimaryCtaText, pdpSecondaryCtaText, pdpSecondaryCtaUrl } =
      contentfulData;

    const trackCtaInteraction = (text, type) =>
      trackBqPDPInteraction({
        id: "quiz:bra-size",
        type,
        text,
        attributes: {
          details: "results"
        }
      });

    const handleCompleteQuiz = () => {
      const { numberedSize, bandSize } = braQuizResultsData;
      onCompleteQuiz(numberedSize, bandSize);
      trackCtaInteraction("shop-this-bra", "Button");
    };

    const publicConfigProps = {
      host,
      apiHost,
      proxyApiHost,
      cartUrl: links?.cart
    };

    return (
      <div className={styles.braQuizResultsEmbedded}>
        {resultsBody}
        <div className={styles.braQuizResultsButtons}>
          <Button
            className={styles.braQuizResultsPrimaryCTA}
            data-testid="primary-cta-embedded-quiz"
            onClick={handleCompleteQuiz}
          >
            {pdpPrimaryCtaText}
          </Button>
          {/* TODO: change <a> to <LinkGenerator /> once v3 migration is complete */}
          <a
            className={styles.braQuizResultsSecondaryCTA}
            data-testid="secondary-cta-embedded-quiz"
            href={braQuizResultsData?.cdpURL || pdpSecondaryCtaUrl}
            onClick={() => trackCtaInteraction("shop-recommended-bras", "Link")}
            {...publicConfigProps}
          >
            {pdpSecondaryCtaText}
          </a>
        </div>
      </div>
    );
  }

  if (!isMobile) {
    return (
      <div
        className={cs("center-xs", styles.braQuizResults)}
        data-testid="bra-quiz-results"
      >
        <div className="row">
          <div className="col-xs-12">{resultsBody}</div>
        </div>
      </div>
    );
  }

  const { isVisible, onCloseModal } = modalProps || {};
  const { secondaryCallToAction, primaryCallToAction } = contentfulData;
  return (
    <LazyHydrate whenIdle style={null} role="status" aria-live="polite">
      <Modal
        messages={{ close: "Close" }}
        visible={isVisible}
        onRequestClose={onCloseModal}
        aria-labelledby="bra-quiz-modal-title"
        classes={{
          container: styles.braQuizResultsModalContainer
        }}
        data-testid="bra-quiz-modal"
      >
        {resultsBody}
        {secondaryCallToAction &&
          (truefitAtCDPABFlag ? (
            <Link
              className="tfc-fitrec-register"
              data-category="Bras"
              data-locale="en_US"
              direction="right"
              tag="div"
              variant="arrowUnderline"
            >
              {secondaryCallToAction.fields.callToAction}
            </Link>
          ) : (
            <CTALink
              className={styles.braQuizResultsSecondaryCTA}
              kind="secondary"
              href={secondaryCallToAction.fields.webRoute}
            >
              {secondaryCallToAction.fields.callToAction}
            </CTALink>
          ))}
        <hr className={styles.braQuizResultsSeparator} />
        {primaryCallToAction && (
          <Button
            variant="filledImpact"
            className={styles.braQuizResultsPrimaryCTA}
            onMouseUp={onCloseModal}
          >
            {primaryCallToAction.fields.callToAction}
          </Button>
        )}
      </Modal>
    </LazyHydrate>
  );
};

BraQuizResults.defaultProps = {
  modalProps: {
    isVisible: false,
    onCloseModal: () => {}
  },
  isMobile: false,
  isEmbedded: false,
  onCompleteQuiz: null,
  truefitAtCDPABFlag: false
};

BraQuizResults.propTypes = {
  braQuizResultsData: PropTypes.shape({
    americanSize: PropTypes.string,
    bandSize: PropTypes.string,
    coverages: PropTypes.arrayOf(PropTypes.string),
    letterSizeLabel: PropTypes.string,
    letteredSize: PropTypes.string,
    numberedSize: PropTypes.string,
    numberedSizeLabel: PropTypes.string,
    cdpURL: PropTypes.string
  }).isRequired,
  contentfulData: PropTypes.shape({
    description: PropTypes.string,
    primaryCallToAction: PropTypes.shape({
      fields: PropTypes.shape({
        webRoute: PropTypes.string,
        callToAction: PropTypes.string
      })
    }),
    secondaryCallToAction: PropTypes.shape({
      fields: PropTypes.shape({
        webRoute: PropTypes.string,
        callToAction: PropTypes.string
      })
    }),
    pdpDrawerTitle: PropTypes.string,
    pdpDrawerSubtitle: PropTypes.string,
    pdpPrimaryCtaText: PropTypes.string,
    pdpSecondaryCtaText: PropTypes.string,
    pdpSecondaryCtaUrl: PropTypes.string,
    title: PropTypes.string
  }).isRequired,
  modalProps: PropTypes.shape({
    isVisible: PropTypes.bool,
    onCloseModal: PropTypes.func
  }),
  isMobile: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  onCompleteQuiz: PropTypes.func,
  truefitAtCDPABFlag: PropTypes.bool
};

export default BraQuizResults;
